import type {
  AIAgent,
  AIConnector,
} from "@p0security/shared/types/ai-inventory";

import type { ManagedMcpServer } from "../hooks/useManagedMcpServers.ts";

export const RISK_SEVERITIES = ["urgent", "high", "medium", "low"] as const;
export type RiskSeverity = (typeof RISK_SEVERITIES)[number];

export const isRiskSeverity = (value: unknown): value is RiskSeverity =>
  typeof value === "string" &&
  (RISK_SEVERITIES as readonly string[]).includes(value);

export const AGENT_STATUS_VALUES = ["local", "managed", "unmanaged"] as const;
export type AgentStatus = (typeof AGENT_STATUS_VALUES)[number];

export const isAgentStatus = (value: unknown): value is AgentStatus =>
  typeof value === "string" &&
  (AGENT_STATUS_VALUES as readonly string[]).includes(value);

/**
 * Single source of truth for the inventory's three-way agent bucketing:
 * local-agents go to "local" regardless of connector state; everything else
 * splits on whether any connector bypasses the P0 gateway.
 */
export const agentStatusOf = (
  agent: AIAgent,
  unmanagedConnectorCount: number
): AgentStatus =>
  agent.platform === "local-agents"
    ? "local"
    : unmanagedConnectorCount > 0
      ? "unmanaged"
      : "managed";

/**
 * Optional governance-policy mapping for a risk. When set, the UI can
 * display badges + tooltips referencing OWASP ASI and NIST AI RMF — letting
 * a CISO trace each finding to a recognized standard.
 */
export type RiskStandards = {
  readonly owaspAsi: readonly string[];
  readonly nistAiRmf: readonly string[];
};

export type Risk = {
  readonly id: string;
  readonly severity: RiskSeverity;
  readonly title: string;
  readonly description: string;
  readonly connectorId: string;
  readonly toolId?: string;
  readonly standards?: RiskStandards;
};

export type PolicySummary = {
  readonly id: string;
  readonly name: string;
  readonly enabled: boolean;
};

export type SessionSummary = {
  readonly id: string;
  /** MCP message category, derived from `event.message.method`. */
  readonly kind?: "request" | "tool-call";
  readonly userEmail: string;
  readonly toolName: string;
  /** Timestamp of the request event for this message, as ISO 8601. */
  readonly eventAt: string;
  readonly outcome: "denied" | "error" | "ok";
  readonly durationMs: number;
  /** P0 access request ID */
  readonly requestId?: string;
  /** Check tool status value */
  readonly requestStatus?: string;
  /** Downstream MCP server the call was routed to (event.mcp-server.id). */
  readonly serverId?: string;
};

export type ConnectorWithContext = {
  readonly connector: AIConnector;
  /** Routes through the P0 MCP gateway (gated + policy-enforced). */
  readonly managed: boolean;
  readonly risks: Risk[];
  readonly tools: ReadonlyArray<{ id: string; name: string }>;
};

export type P0MCPContext = {
  readonly enforcingCount: number;
  readonly policies: readonly PolicySummary[];
  /** Full managed-server registry, surfaced in the Gateway popover. */
  readonly managedServers: readonly ManagedMcpServer[];
};

export type AgentGraphData = {
  readonly agent: AIAgent;
  readonly p0Mcp: P0MCPContext | null;
  readonly connectors: ConnectorWithContext[];
  /**
   * Managed MCP servers fronted by the Gateway for this tenant. Each is
   * rendered as its own node; labels come from the Firestore registry.
   */
  readonly mcpServers: readonly ManagedMcpServer[];
  /** Risks attributed to each managed MCP server, keyed by server.id. */
  readonly serverRisks: Readonly<Record<string, Risk[]>>;
};
